import { ExperienceConfig, CreateExperienceResponse, GetExperienceResponse } from '../shared/types';

// Storage keys
const STORAGE_KEYS = {
    SESSION_ID: 'valentine_session_id',
    PUBLIC_TOKEN: 'valentine_public_token',
};

export interface TownState {
    buildings: Array<{ slot: string; type: string; level: number }>;
    decorations: string[];
    tier: 'basic' | 'premium';
}

export interface SessionResponse {
    sessionId: string;
    publicToken: string;
    createdAt: number;
}

export interface SessionData {
    sessionId: string;
    publicToken: string;
    createdAt: number;
    answers: Record<string, string>;
}

export interface AnswerResponse {
    success: boolean;
    questionKey: string;
    answerValue: string;
    createdAt: number;
}

export interface PublicSummary {
    createdAt: number;
    answers: Record<string, string>;
    townState: TownState;
}

class ExperienceServiceReal {
    private EXPERIENCE_API = '/api/experience';
    private SESSION_API = '/api/session';
    private ANSWER_API = '/api/answer';

    // --- Experience Management ---

    async createExperience(config: Omit<ExperienceConfig, 'id' | 'createdAt' | 'paymentStatus' | 'isActive'>): Promise<CreateExperienceResponse> {
        try {
            const response = await fetch(this.EXPERIENCE_API, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(config),
            });
            const data = await response.json() as CreateExperienceResponse;
            if (!data.success) {
                console.error('API Error Details:', data);
            }
            return data;
        } catch (error) {
            console.error('Create Experience Error:', error);
            return { success: false, error: 'Network error occurred' };
        }
    }

    async getExperienceBySubdomain(subdomain: string): Promise<GetExperienceResponse> {
        try {
            const response = await fetch(`${this.EXPERIENCE_API}?subdomain=${encodeURIComponent(subdomain)}`);

            if (!response.ok) {
                if (response.status === 404) {
                    return { success: false, error: 'Experience not found' };
                }
                if (response.status === 402) {
                    return { success: false, error: 'Payment Required', requiresPayment: true };
                }
                const errorData = await response.json() as any;
                return { success: false, error: errorData.error || 'Failed to fetch experience' };
            }

            return await response.json();
        } catch (error) {
            console.error('Get Experience Error:', error);
            return { success: false, error: 'Network error occurred' };
        }
    }

    async getExperienceById(id: string): Promise<GetExperienceResponse> {
        try {
            const response = await fetch(`${this.EXPERIENCE_API}?id=${encodeURIComponent(id)}`);
            if (!response.ok) return { success: false, error: 'Not found' };
            return await response.json();
        } catch (error) {
            return { success: false, error: 'Network error' };
        }
    }

    async updateExperience(id: string, editToken: string, updates: Partial<ExperienceConfig>): Promise<{ success: boolean; error?: string }> {
        try {
            const response = await fetch(this.EXPERIENCE_API, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id, editToken, ...updates })
            });
            return await response.json();
        } catch (error) {
            return { success: false, error: 'Network error' };
        }
    }

    // --- Session Management ---

    async createSession(): Promise<SessionResponse> {
        const response = await fetch(this.SESSION_API, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) {
            throw new Error('Failed to create session');
        }

        const data = (await response.json()) as SessionResponse;

        // Store in localStorage
        localStorage.setItem(STORAGE_KEYS.SESSION_ID, data.sessionId);
        localStorage.setItem(STORAGE_KEYS.PUBLIC_TOKEN, data.publicToken);

        return data;
    }

    getStoredSessionId(): string | null {
        return localStorage.getItem(STORAGE_KEYS.SESSION_ID);
    }

    getStoredPublicToken(): string | null {
        return localStorage.getItem(STORAGE_KEYS.PUBLIC_TOKEN);
    }

    async rehydrateSession(sessionId: string): Promise<SessionData> {
        const response = await fetch(`${this.SESSION_API}/${sessionId}`);

        if (!response.ok) {
            throw new Error('Failed to rehydrate session');
        }

        return (await response.json()) as SessionData;
    }

    async saveAnswer(sessionId: string, questionKey: string, answerValue: string): Promise<AnswerResponse> {
        const response = await fetch(this.ANSWER_API, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sessionId, questionKey, answerValue }),
        });

        if (!response.ok) {
            throw new Error('Failed to save answer');
        }

        return (await response.json()) as AnswerResponse;
    }

    async getPublicSummary(publicToken: string): Promise<PublicSummary> {
        // We implemented public route inside session.ts as /api/session/public/:token
        // Or we should assume /api/session/public/:token based on my worker edit?
        // Wait, I put it as app.get('/public/:token') inside session.ts, which is mounted at /api/session
        // So the path is /api/session/public/:token
        const response = await fetch(`${this.SESSION_API}/public/${publicToken}`);

        if (!response.ok) {
            throw new Error('Failed to get public summary');
        }

        return (await response.json()) as PublicSummary;
    }



    async notifySharing(experienceId: string, publicToken: string): Promise<{ success: boolean; error?: string }> {
        try {
            const response = await fetch(`${this.EXPERIENCE_API}/notify-share`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: experienceId, publicToken })
            });
            return await response.json();
        } catch (error) {
            console.error('Notify Sharing Error:', error);
            return { success: false, error: 'Network error' };
        }
    }

    clearSession(): void {
        localStorage.removeItem(STORAGE_KEYS.SESSION_ID);
        localStorage.removeItem(STORAGE_KEYS.PUBLIC_TOKEN);
    }

    getShareUrl(publicToken: string): string {
        const url = new URL(window.location.href);
        url.pathname = '/';
        url.searchParams.set('share', publicToken);
        return url.toString();
    }

    async copyShareUrl(publicToken: string): Promise<boolean> {
        try {
            await navigator.clipboard.writeText(this.getShareUrl(publicToken));
            return true;
        } catch {
            return false;
        }
    }
}

export const ExperienceService = new ExperienceServiceReal();
